import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { buildAndDownloadApkg } from '../../utils/anki';
import '../../App.css';
import type { PopupState, Token } from '../../types';
import TokenRenderer from './TokenRenderer';
import WordPopup from './WordPopup';
import SaveWordModal, { BatchSaveModal } from './SaveWordModal';

const VOCAB_PAGE_SIZE = 20;

export default function ArticleView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { articles } = useAppContext();
  const article = articles.find(a => a.id === id);

  const [popup, setPopup] = useState<PopupState | null>(null);
  const [showFurigana, setShowFurigana] = useState(false);
  const [saveToken, setSaveToken] = useState<{ token: Token; tokenIndex?: number } | null>(null);
  const [showBatchSave, setShowBatchSave] = useState(false);
  const [ankiDownloading, setAnkiDownloading] = useState(false);
  const [vocabPage, setVocabPage] = useState(0);

  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const lastTokenIdxRef = useRef<number>(-1);

  const clearHighlight = useCallback(() => {
    contentRef.current?.querySelectorAll('.char-highlight').forEach(n => n.classList.remove('char-highlight'));
    titleRef.current?.querySelectorAll('.char-highlight').forEach(n => n.classList.remove('char-highlight'));
    lastTokenIdxRef.current = -1;
    setPopup(null);
  }, []);

  const handleTokenClick = useCallback((e: React.MouseEvent<HTMLDivElement>, containerRef: React.RefObject<HTMLDivElement | null>, tokens: Token[]) => {
    const el = containerRef.current;
    if (!el) return;
    const tokenEl = (e.target as HTMLElement).closest('[data-ti]');
    if (!tokenEl) {
      clearHighlight();
      return;
    }
    e.stopPropagation();
    const ti = parseInt((tokenEl as HTMLElement).getAttribute('data-ti')!, 10);
    if (lastTokenIdxRef.current === ti && containerRef === contentRef) {
      clearHighlight();
      return;
    }
    // Clear highlights from both containers
    contentRef.current?.querySelectorAll('.char-highlight').forEach(n => n.classList.remove('char-highlight'));
    titleRef.current?.querySelectorAll('.char-highlight').forEach(n => n.classList.remove('char-highlight'));
    el.querySelectorAll(`[data-ti="${ti}"]`).forEach(n => n.classList.add('char-highlight'));
    lastTokenIdxRef.current = ti;
    const token = tokens[ti];
    if (token.translation) {
      const firstChar = el.querySelector(`[data-ti="${ti}"]`) as HTMLElement | null;
      const rect = firstChar?.getBoundingClientRect();
      if (rect) {
        setPopup({
          token,
          left: Math.min(rect.left, window.innerWidth - 340),
          bottom: window.innerHeight - rect.top + 8,
        });
      }
    } else {
      setPopup(null);
    }
  }, [clearHighlight]);

  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!article) return;
    handleTokenClick(e, contentRef, article.tokens);
  }, [article, handleTokenClick]);

  const handleTitleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!article?.titleTokens) return;
    handleTokenClick(e, titleRef, article.titleTokens);
  }, [article, handleTokenClick]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (contentRef.current?.contains(target) || titleRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      clearHighlight();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [clearHighlight]);

  if (!article) {
    return (
      <div className="p-4 pt-14 md:p-8 text-center">
        <p className="text-slate-500 dark:text-slate-400">Article not found.</p>
        <button onClick={() => navigate('/app/articles')} className="mt-4 text-primary-600 hover:text-primary-700 text-sm">
          Back to Articles
        </button>
      </div>
    );
  }

  // Get all unique tokens with translations (not just kanji)
  const seen = new Set<string>();
  const uniqueVocab = article.tokens.filter(t => {
    if (!t.translation || t.surface === '\n') return false;
    const key = t.basicForm || t.surface;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(uniqueVocab.length / VOCAB_PAGE_SIZE);
  const pagedVocab = uniqueVocab.slice(vocabPage * VOCAB_PAGE_SIZE, (vocabPage + 1) * VOCAB_PAGE_SIZE);

  const handleDownloadAnki = async () => {
    setAnkiDownloading(true);
    try {
      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      await buildAndDownloadApkg(uniqueVocab, article.title, `article_${stamp}.apkg`);
    } finally {
      setAnkiDownloading(false);
    }
  };

  // Find token index in article tokens for a vocab item
  const findTokenIndex = (token: Token): number => {
    return article.tokens.findIndex(t => t.surface === token.surface);
  };

  return (
    <div className="p-4 pt-14 md:p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => { navigate('/app/articles'); setPopup(null); }}
          className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={() => setShowFurigana(f => !f)}
          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
            showFurigana
              ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/60'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
          }`}
        >
          {showFurigana ? 'Hide Furigana' : 'Show Furigana'}
        </button>
      </div>

      {/* Clickable title */}
      <div
        className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4 cursor-pointer"
        ref={titleRef}
        onClick={handleTitleClick}
      >
        {article.titleTokens ? (
          <TokenRenderer tokens={article.titleTokens} showFurigana={showFurigana} />
        ) : (
          article.title
        )}
      </div>

      <div
        className="article-content text-base leading-[1.8] max-w-[42em] mb-8 text-slate-800 dark:text-slate-200"
        ref={contentRef}
        onClick={handleContentClick}
        role="article"
      >
        <TokenRenderer tokens={article.tokens} showFurigana={showFurigana} />
      </div>

      {uniqueVocab.length > 0 && (
        <section className="border-t border-slate-200 dark:border-slate-700 pt-6 max-w-[42em]">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Vocabulary ({uniqueVocab.length} words)</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBatchSave(true)}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-md transition-colors"
              >
                Add All to Deck
              </button>
              <button
                onClick={handleDownloadAnki}
                disabled={ankiDownloading}
                className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded-md transition-colors disabled:bg-slate-300 dark:disabled:bg-slate-600"
              >
                {ankiDownloading ? 'Preparing...' : 'Download Anki (.apkg)'}
              </button>
            </div>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {pagedVocab.map((t, i) => (
              <li key={`${t.basicForm || t.surface}-${i}`} className="grid grid-cols-[auto_auto_1fr_auto] gap-x-4 py-1.5 text-sm items-baseline">
                <span className="font-semibold text-slate-800 dark:text-slate-100 min-w-[2.5em]">{t.basicForm || t.surface}</span>
                <span className="text-slate-500 dark:text-slate-400">{t.reading}</span>
                <span className="text-slate-400 truncate">{t.translation || '—'}</span>
                <button
                  onClick={() => setSaveToken({ token: t, tokenIndex: findTokenIndex(t) })}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-medium px-1"
                  title="Save to deck"
                >
                  +
                </button>
              </li>
            ))}
          </ul>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
              <span className="text-xs text-slate-400">
                {vocabPage * VOCAB_PAGE_SIZE + 1}–{Math.min((vocabPage + 1) * VOCAB_PAGE_SIZE, uniqueVocab.length)} of {uniqueVocab.length}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setVocabPage(p => Math.max(0, p - 1))}
                  disabled={vocabPage === 0}
                  className="px-2.5 py-1 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>
                <button
                  onClick={() => setVocabPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={vocabPage >= totalPages - 1}
                  className="px-2.5 py-1 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {popup && (
        <WordPopup
          popup={popup}
          popupRef={popupRef}
          onSave={(token) => {
            const ti = article.tokens.findIndex(t => t === popup.token);
            setSaveToken({ token, tokenIndex: ti >= 0 ? ti : undefined });
          }}
          onMouseLeave={(e) => {
            const related = e.relatedTarget;
            if (!(related instanceof Node) || (!contentRef.current?.contains(related) && !titleRef.current?.contains(related))) clearHighlight();
          }}
        />
      )}

      {saveToken && (
        <SaveWordModal
          token={saveToken.token}
          onClose={() => setSaveToken(null)}
          articleTokens={article.tokens}
          tokenIndex={saveToken.tokenIndex}
        />
      )}

      {showBatchSave && (
        <BatchSaveModal
          tokens={uniqueVocab}
          onClose={() => setShowBatchSave(false)}
          articleTokens={article.tokens}
        />
      )}
    </div>
  );
}
